import React, { useEffect, useState } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { facilitiesAPI } from '@/lib/api';
import { FacilityUpcomingMatches } from '@/components/FacilityUpcomingMatches';
import { useAuth } from '@/contexts/AuthContext';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Building2, MapPin, Globe, Phone, Mail, ExternalLink,
  ShieldCheck, CheckCircle2, ArrowLeft, Calendar,
} from 'lucide-react';
import { resolveMediaUrl } from '@/lib/utils';

const AMENITY_LABELS = {
  lockers: { label: 'Spogliatoi', icon: '🚿' },
  showers: { label: 'Docce', icon: '🛁' },
  parking: { label: 'Parcheggio', icon: '🅿️' },
  wifi: { label: 'Wi-Fi', icon: '📶' },
  bar: { label: 'Bar / Ristoro', icon: '🍻' },
  first_aid: { label: 'Primo soccorso', icon: '🚑' },
  wheelchair_access: { label: 'Accesso disabili', icon: '♿' },
  equipment_rental: { label: 'Noleggio attrezzatura', icon: '🎒' },
};

const DAYS = [
  { key: 'mon', label: 'Lunedì' },
  { key: 'tue', label: 'Martedì' },
  { key: 'wed', label: 'Mercoledì' },
  { key: 'thu', label: 'Giovedì' },
  { key: 'fri', label: 'Venerdì' },
  { key: 'sat', label: 'Sabato' },
  { key: 'sun', label: 'Domenica' },
];

// Set/restore lightweight SEO meta tags
const setMeta = (name, content) => {
  if (!content) return null;
  let el = document.head.querySelector(`meta[name="${name}"]`);
  let created = false;
  if (!el) {
    el = document.createElement('meta');
    el.setAttribute('name', name);
    document.head.appendChild(el);
    created = true;
  }
  const previous = el.getAttribute('content');
  el.setAttribute('content', content);
  return () => {
    if (created) el.remove();
    else if (previous != null) el.setAttribute('content', previous);
  };
};

const setOg = (property, content) => {
  if (!content) return null;
  let el = document.head.querySelector(`meta[property="${property}"]`);
  let created = false;
  if (!el) {
    el = document.createElement('meta');
    el.setAttribute('property', property);
    document.head.appendChild(el);
    created = true;
  }
  const previous = el.getAttribute('content');
  el.setAttribute('content', content);
  return () => {
    if (created) el.remove();
    else if (previous != null) el.setAttribute('content', previous);
  };
};

// Schema.org JSON-LD for SportsActivityLocation
const setStructuredData = (facility) => {
  if (!facility) return null;
  const data = {
    '@context': 'https://schema.org',
    '@type': 'SportsActivityLocation',
    name: facility.name,
    description: facility.description || undefined,
    address: facility.address ? {
      '@type': 'PostalAddress',
      streetAddress: facility.address,
      addressLocality: facility.city,
      addressCountry: 'IT',
    } : undefined,
    geo: facility.lat && facility.lng ? {
      '@type': 'GeoCoordinates',
      latitude: facility.lat,
      longitude: facility.lng,
    } : undefined,
    image: facility.cover_image_url ? resolveMediaUrl(facility.cover_image_url) : undefined,
    telephone: facility.phone || undefined,
    email: facility.email || undefined,
    url: typeof window !== 'undefined' ? window.location.href : undefined,
  };
  const script = document.createElement('script');
  script.type = 'application/ld+json';
  script.id = 'leaguehub-facility-jsonld';
  script.textContent = JSON.stringify(data);
  document.head.appendChild(script);
  return () => script.remove();
};

const PublicFacilityPage = () => {
  const { facilityId } = useParams();
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();
  const [loading, setLoading] = useState(true);
  const [facility, setFacility] = useState(null);
  const [fields, setFields] = useState([]);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    const fetchData = async () => {
      try {
        const res = await facilitiesAPI.getById(facilityId);
        if (cancelled) return;
        setFacility(res.data.facility);
        setFields(res.data.fields || []);
      } catch (err) {
        if (!cancelled) setError(err);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    fetchData();
    return () => { cancelled = true; };
  }, [facilityId]);

  // SEO meta + schema.org
  useEffect(() => {
    if (!facility) return;
    const title = `${facility.name}${facility.city ? ' — ' + facility.city : ''} · LeagueHub`;
    const previousTitle = document.title;
    document.title = title;

    const description = facility.description
      ? facility.description.slice(0, 160)
      : `Prenota campi da gioco presso ${facility.name}${facility.city ? ' a ' + facility.city : ''} su LeagueHub.`;

    const cleanups = [
      setMeta('description', description),
      setOg('og:title', title),
      setOg('og:description', description),
      setOg('og:type', 'website'),
      facility.cover_image_url && setOg('og:image', resolveMediaUrl(facility.cover_image_url)),
      setStructuredData(facility),
    ].filter(Boolean);

    return () => {
      document.title = previousTitle;
      cleanups.forEach((fn) => fn && fn());
    };
  }, [facility]);

  const goBook = () => {
    if (isAuthenticated) {
      // Already logged in — go straight to creating a match here
      navigate(`/matches/new?facility=${facilityId}`);
    } else {
      // Push them to landing with a return hint
      navigate(`/?facility=${facilityId}`);
    }
  };

  if (loading) {
    return (
      <div className="safe-public-page">
        <div className="max-w-5xl mx-auto p-4 md:p-6 space-y-4">
          <Skeleton className="h-72 w-full rounded-2xl" />
          <Skeleton className="h-8 w-2/3" />
          <Skeleton className="h-4 w-1/2" />
          <div className="grid grid-cols-3 gap-4">
            <Skeleton className="h-32 col-span-2" />
            <Skeleton className="h-32" />
          </div>
        </div>
      </div>
    );
  }

  if (error || !facility) {
    return (
      <div className="safe-public-page">
        <div className="max-w-3xl mx-auto p-8 text-center">
          <Building2 className="h-12 w-12 mx-auto text-muted-foreground mb-3" />
          <h1 className="text-2xl font-bold">Struttura non trovata</h1>
          <p className="text-muted-foreground mt-2">Il link potrebbe essere sbagliato o la struttura non è più disponibile.</p>
          <Button asChild className="mt-4">
            <Link to="/strutture">Vedi tutte le strutture</Link>
          </Button>
        </div>
      </div>
    );
  }

  const activeAmenities = Object.entries(facility.amenities || {})
    .filter(([, v]) => v)
    .map(([key]) => ({ key, ...AMENITY_LABELS[key] }))
    .filter((a) => a.label);

  const minPrice = fields
    .map((f) => Number(f.hourly_rate || 0))
    .filter((p) => p > 0)
    .reduce((m, p) => (m === null || p < m ? p : m), null);

  return (
    <div className="safe-public-page bg-background">
      {/* Slim public header */}
      <header className="border-b bg-card">
        <div className="max-w-6xl mx-auto px-4 py-3 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2 font-bold">
            <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-emerald-500 to-blue-600 flex items-center justify-center text-white">L</div>
            LeagueHub
          </Link>
          <div className="flex items-center gap-3 text-sm">
            <Link to="/strutture" className="text-muted-foreground hover:text-foreground hidden sm:flex items-center gap-1">
              <ArrowLeft className="h-3.5 w-3.5" />
              Marketplace
            </Link>
            {!isAuthenticated && (
              <Button asChild size="sm" className="bg-gradient-to-br from-emerald-500 to-blue-600">
                <Link to="/">Accedi / Registrati</Link>
              </Button>
            )}
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-4 py-6 space-y-6">
        {/* Hero with cover */}
        <div className="relative rounded-2xl overflow-hidden h-64 md:h-80 bg-gradient-to-br from-emerald-400 to-blue-600">
          {facility.cover_image_url && (
            <img
              src={resolveMediaUrl(facility.cover_image_url)}
              alt={facility.name}
              className="absolute inset-0 w-full h-full object-cover"
            />
          )}
          <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent" />
          <div className="relative h-full flex flex-col justify-end p-6 text-white">
            <div className="flex items-center gap-2 flex-wrap mb-2">
              {facility.is_verified && (
                <Badge className="bg-emerald-600 hover:bg-emerald-600 text-white">
                  <ShieldCheck className="h-3 w-3 mr-1" /> Verificato
                </Badge>
              )}
              {facility.tier === 'premium' && (
                <Badge className="bg-purple-600 hover:bg-purple-600 text-white">⭐ Premium</Badge>
              )}
            </div>
            <h1 className="text-3xl md:text-4xl font-bold leading-tight drop-shadow">{facility.name}</h1>
            {facility.city && (
              <div className="text-sm opacity-95 mt-1 flex items-center gap-1">
                <MapPin className="h-4 w-4" />
                {facility.address || facility.city}
              </div>
            )}
          </div>
        </div>

        <FacilityUpcomingMatches facilityId={facility.facility_id} />

        {/* Layout: main + sticky sidebar */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <div className="lg:col-span-2 space-y-6">
            {/* Description */}
            {facility.description && (
              <Card>
                <CardContent className="p-5">
                  <h2 className="font-bold mb-2">Descrizione</h2>
                  <p className="text-muted-foreground whitespace-pre-line">{facility.description}</p>
                </CardContent>
              </Card>
            )}

            {/* Amenities */}
            {activeAmenities.length > 0 && (
              <Card>
                <CardContent className="p-5">
                  <h2 className="font-bold mb-3">Servizi</h2>
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                    {activeAmenities.map((a) => (
                      <div key={a.key} className="flex items-center gap-2 p-2 rounded-md bg-muted/50">
                        <span className="text-lg">{a.icon}</span>
                        <span className="text-sm">{a.label}</span>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Fields */}
            {fields.length > 0 && (
              <Card>
                <CardContent className="p-5">
                  <h2 className="font-bold mb-3">Campi disponibili ({fields.length})</h2>
                  <div className="space-y-2">
                    {fields.map((f) => (
                      <div
                        key={f.field_id}
                        className="flex items-center justify-between p-3 rounded-lg border"
                      >
                        <div>
                          <div className="font-semibold">{f.name}</div>
                          <div className="text-xs text-muted-foreground">
                            {(f.sport_names || []).join(', ')}{' '}
                            {f.surface_type ? `· ${f.surface_type}` : ''}
                          </div>
                        </div>
                        {f.hourly_rate && (
                          <div className="text-right">
                            <div className="text-xs text-muted-foreground">da</div>
                            <div className="font-bold text-emerald-600">€{f.hourly_rate}/h</div>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Opening hours */}
            {Object.keys(facility.opening_hours || {}).length > 0 && (
              <Card>
                <CardContent className="p-5">
                  <h2 className="font-bold mb-3">Orari</h2>
                  <div className="space-y-1 text-sm">
                    {DAYS.map((d) => {
                      const h = (facility.opening_hours || {})[d.key];
                      if (!h) return null;
                      return (
                        <div key={d.key} className="flex justify-between">
                          <span className="text-muted-foreground">{d.label}</span>
                          <span className="font-medium">
                            {h.closed ? 'Chiuso' : `${h.open || '08:00'} – ${h.close || '23:00'}`}
                          </span>
                        </div>
                      );
                    })}
                  </div>
                </CardContent>
              </Card>
            )}

          </div>

          {/* Sticky booking sidebar */}
          <aside className="lg:col-span-1">
            <div className="lg:sticky lg:top-6 space-y-3">
              <Card className="border-2 border-emerald-200 dark:border-emerald-900">
                <CardContent className="p-5">
                  {minPrice && (
                    <>
                      <div className="text-xs text-muted-foreground">A partire da</div>
                      <div className="text-3xl font-bold text-emerald-600">€{minPrice}<span className="text-base text-muted-foreground font-normal">/h</span></div>
                    </>
                  )}
                  {!minPrice && (
                    <div className="font-semibold">Disponibilità su richiesta</div>
                  )}
                  <Button
                    onClick={goBook}
                    className="mt-4 w-full bg-gradient-to-br from-emerald-500 to-emerald-600 hover:opacity-90 font-bold"
                  >
                    <Calendar className="h-4 w-4 mr-2" />
                    {isAuthenticated ? 'Prenota subito' : 'Accedi per prenotare'}
                  </Button>
                  <ul className="mt-4 space-y-1.5 text-xs text-muted-foreground">
                    <li className="flex gap-2"><CheckCircle2 className="h-3.5 w-3.5 text-emerald-600" /> Conferma istantanea</li>
                    <li className="flex gap-2"><CheckCircle2 className="h-3.5 w-3.5 text-emerald-600" /> Pagamento sicuro Stripe</li>
                    <li className="flex gap-2"><CheckCircle2 className="h-3.5 w-3.5 text-emerald-600" /> Annullabile fino a 24h prima</li>
                  </ul>
                </CardContent>
              </Card>

              {/* Contacts */}
              {(facility.phone || facility.email || facility.website) && (
                <Card>
                  <CardContent className="p-5 space-y-2 text-sm">
                    <div className="font-semibold mb-1">Contatti</div>
                    {facility.phone && (
                      <a href={`tel:${facility.phone}`} className="flex items-center gap-2 text-foreground hover:underline">
                        <Phone className="h-4 w-4 text-muted-foreground" />
                        {facility.phone}
                      </a>
                    )}
                    {facility.email && (
                      <a href={`mailto:${facility.email}`} className="flex items-center gap-2 text-foreground hover:underline break-all">
                        <Mail className="h-4 w-4 text-muted-foreground shrink-0" />
                        {facility.email}
                      </a>
                    )}
                    {facility.website && (
                      <a href={facility.website} target="_blank" rel="noreferrer" className="flex items-center gap-2 text-foreground hover:underline break-all">
                        <Globe className="h-4 w-4 text-muted-foreground shrink-0" />
                        Sito web
                        <ExternalLink className="h-3 w-3" />
                      </a>
                    )}
                  </CardContent>
                </Card>
              )}

              {/* Map link */}
              {(facility.lat && facility.lng) && (
                <Card>
                  <CardContent className="p-5">
                    <div className="font-semibold mb-2 text-sm">Posizione</div>
                    <a
                      href={facility.map_url || `https://www.openstreetmap.org/?mlat=${facility.lat}&mlon=${facility.lng}`}
                      target="_blank"
                      rel="noreferrer"
                      className="block aspect-video rounded-lg bg-gradient-to-br from-emerald-100 to-blue-100 dark:from-emerald-950/40 dark:to-blue-950/40 flex items-center justify-center text-sm font-medium hover:opacity-90"
                    >
                      <MapPin className="h-5 w-5 mr-2 text-emerald-600" />
                      Apri in mappe
                      <ExternalLink className="h-3.5 w-3.5 ml-1" />
                    </a>
                  </CardContent>
                </Card>
              )}

            </div>
          </aside>
        </div>
      </main>

      <footer className="border-t bg-card mt-10 py-6 text-center text-xs text-muted-foreground">
        © LeagueHub · <Link to="/strutture" className="hover:underline">Marketplace strutture</Link> · <Link to="/business" className="hover:underline">Sei una struttura?</Link>
      </footer>
    </div>
  );
};

export default PublicFacilityPage;
